import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useSocialAccounts } from '../hooks/useSocialAccounts';
import { useSocialPosts } from '../hooks/useSocialPosts';
import { PostsTable } from '../components/PostsTable';
import { AccountCard } from '../components/AccountCard';
import { PlusCircle, AlertCircle } from 'lucide-react';

export function DashboardPage() {
  const navigate = useNavigate();
  const { accounts, isLoading: accountsLoading, disconnectAccount } = useSocialAccounts();
  const { posts: scheduledPosts } = useSocialPosts('scheduled');
  const { posts: recentPosts } = useSocialPosts();

  const activeAccounts = accounts.filter((a) => a.status === 'active');
  const errorAccounts = accounts.filter((a) => a.status !== 'active');

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Social Media</h1>
        <Button onClick={() => navigate('/a/arche_social_media/compose')} className="gap-2">
          <PlusCircle className="h-4 w-4" />
          Create Post
        </Button>
      </div>

      {/* Account Health */}
      {!accountsLoading && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Connected Accounts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{activeAccounts.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Scheduled Posts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{scheduledPosts.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Account Issues
              </CardTitle>
            </CardHeader>
            <CardContent className="flex items-center gap-2">
              <p className="text-3xl font-bold">{errorAccounts.length}</p>
              {errorAccounts.length > 0 && <AlertCircle className="h-5 w-5 text-destructive" />}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Error accounts callout */}
      {errorAccounts.length > 0 && (
        <div className="p-4 border border-destructive/50 rounded-lg bg-destructive/5 space-y-3">
          <p className="text-sm font-medium text-destructive">
            {errorAccounts.length} account{errorAccounts.length > 1 ? 's need' : ' needs'} attention
          </p>
          {errorAccounts.map((a) => (
            <AccountCard key={a.accountId} account={a} onDisconnect={disconnectAccount} />
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/a/arche_social_media/accounts')}
          >
            Manage Accounts
          </Button>
        </div>
      )}

      {/* Scheduled Posts */}
      {scheduledPosts.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Upcoming</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/a/arche_social_media/scheduled')}
            >
              View all
            </Button>
          </div>
          <div className="space-y-2">
            {scheduledPosts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-4">No posts yet. Create your first post!</p>
                <Button onClick={() => navigate('/a/arche_social_media/compose')}>
                  Create Post
                </Button>
              </div>
            ) : (
              <PostsTable posts={scheduledPosts.slice(0, 10)} />
            )}
          </div>
        </div>
      )}

      {/* Recent Posts */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Recent Posts</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/a/arche_social_media/drafts')}
          >
            View drafts
          </Button>
        </div>
        {recentPosts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">No posts yet. Create your first post!</p>
            <Button onClick={() => navigate('/a/arche_social_media/compose')}>Create Post</Button>
          </div>
        ) : (
          <PostsTable posts={recentPosts.slice(0, 10)} />
        )}
      </div>
    </div>
  );
}
